import { Component, EventEmitter, Output, HostListener, AfterViewInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { File } from '@ionic-native/file/ngx';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import _ from 'lodash';
import math from 'mathjs';
import { Utils } from '../../../service/utils';
import { Constants, BasicObject } from '../../../service/constants';
import { CommonService } from '../../../service/common.service';
import {
  ToolContainer,
  Material_,
  Recommendation_,
  Parameters_,
  Machine_,
  RecommendationLabel,
  ParameterLabel,
  PdfSupportInfo
} from '../../../service/formulas/objects';
import { Formulas } from '../../../service/formulas/formulas';
import { ConversionFormulas } from '../../../service/formulas/conversionsFormulas';
import { Message } from '../../../service/dialog/message.interface';
import { ImageConstants } from '../../../service/image-constants';
import jsPDF from 'jspdf';
import { CacheService } from 'ionic-cache';

@Component({
  selector: 'section-recommendations',
  templateUrl: 'recommendations.html',
  styleUrls: ['recommendations.scss']
})
export class Recommendations implements AfterViewInit {
  private isApp: boolean;
  public windowsDimensions: object = undefined;

  private sliderFormatter = {
    to: (value: number) => {
      const percent = value - 100;
      const prefix = percent > 0 ? '+' : '';
      return `${prefix}${percent}%`;
    },
    from: (value: string) => {
      const percent = Number(value.replace('+%', ''));
      return percent + 100;
    }
  };

  public sliderConfig: any = {
    connect: [true, false],
    start: 100,
    step: 1,
    range: {
      min: 80,
      max: 120
    }
  };

  public sliderConfigDesktop: any = {
    ...this.sliderConfig,
    orientation: 'vertical',
    direction: 'rtl',
    keyboard: true,
    pageSteps: 10,
    pips: {
      mode: 'values',
      values: [80, 85, 90, 95, 100, 105, 110, 115, 120],
      density: 2.5, // 100 / range
      stepped: true,
      format: this.sliderFormatter
    }
  };

  public currentMapMode: number = 1;
  public recommendationAdjustmentOptions: BasicObject[];
  public recommendationAdjustment: number = 0; // default to 0, the code will hardcode what the types are.
  private subscription: Subscription;
  public currenttoolpath: string = '';
  public Recommendations: FormGroup;
  public settingsHolder: ToolContainer;
  private spindleSpeed: number = undefined;
  private feedRate: number = undefined;

  public standardSpeedValue: number = 0;
  public speedAggression: number = 1;
  public resetSpeedActive: boolean = false;

  public standardFeedValue: number = 0;
  public feedAggression: number = 1;
  public resetFeedActive: boolean = false;

  private fixture: number = 0;
  private fixtureChange: boolean = false;
  private machine: Machine_ = new Machine_();
  public recommendation: Recommendation_ = new Recommendation_();
  private updateRecommendations: updateRecommendations = new updateRecommendations();
  private parameters: Parameters_;
  private warningAlerts: boolean = false;
  private warningMessageOpen: boolean = false;

  public mapMode: number;
  public machineTabFilledIn: boolean = false;
  public holderFactor: number;
  public holderFeedFactor: number;
  public spindleFactor: number;
  //variables for generating pdf
  public distName: string = '';
  public distCompany: string = '';
  public endUser: string = '';
  public endUserCompany: string = '';
  public renderRecommendations: boolean = false;
  public headerImage: HTMLImageElement = new Image();
  public helicalImg: HTMLImageElement = new Image();
  public muSymbol: HTMLImageElement = new Image();
  public xTitleLeft: number = 15;
  public xInfoLeft: number = 50;
  public xTitleRight: number = 115;
  public xInfoRight: number = 145;
  public maxLineWidth: number = 65;
  public insideCornerType: string = '';
  public sfmRation: number = 1;
  public fptRation: number = 1;
  public pdfSupportInfo: { [brandKey: number]: PdfSupportInfo[] };

  @Output() recommendationChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() recommendationChangeWithChild: EventEmitter<any> = new EventEmitter<any>();
  @Output() aggressiveWarning: EventEmitter<any> = new EventEmitter<any>();
  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.onResize();
  }

  constructor(
    private androidPermissions: AndroidPermissions,
    private file: File,
    private fileOpener: FileOpener,
    private platform: Platform,
    public utils: Utils,
    private formBuilder: FormBuilder,
    public constants: Constants,
    private commonService: CommonService,
    private formulas: Formulas,
    private conversionsFormulas: ConversionFormulas,
    private cache: CacheService,
    public imageConstants: ImageConstants
  ) {
    this.isApp = this.utils.isApp();
    if (this.isApp) {
      this.cache.getItem('windowsDimensions').then(windowsDimensions => {
        this.windowsDimensions = windowsDimensions;
      });
    }

    this.cache.getItem('pdfSupportInfo').then(info => (this.pdfSupportInfo = info));

    this.recommendationAdjustmentOptions = this.constants.getRecommendationAdjustmentOption();

    this.Recommendations = this.formBuilder.group({
      type1: this.formBuilder.group({
        insideCornerRadiusOfWall: new FormControl('', [Validators.pattern(this.constants.DECIMAL_ONLY_REGEX)]),
        insideCornerType: new FormControl('', [Validators.required])
      }),
      type2: this.formBuilder.group({
        trochoidalSlotWidth: new FormControl('', [Validators.pattern(this.constants.DECIMAL_ONLY_REGEX)])
      }),
      speedInput: this.sliderConfig.start,
      speedSlider: this.sliderConfig.start,
      feedInput: this.sliderConfig.start,
      feedSlider: this.sliderConfig.start
    });

    this.headerImage.src = imageConstants.machiningAdvisorColor;
    this.helicalImg.src = imageConstants.helicalLogo;
    this.muSymbol.src = imageConstants.muSymbol;
  }

  /**
   * @param speedAggression The user-selected multiplier applied to speed
   */
  private updateSpeedAggression = (speedAggression: number) => {
    this.speedAggression = speedAggression;
    this.recommendation.speedAggression = speedAggression;

    // Enable or disable the reset button
    this.resetSpeedActive = speedAggression !== 1;

    // Recalculate based on updated aggression value
    this.recommendation.rpm = this.standardSpeedValue * this.speedAggression;

    this.updateRecommendations.rpm = false;
    this.updateRecommendations.ipm = true;
    this.updateRecommendations.ipmAdjusted = false;
    this.updateRecommendations.rpmAdjusted = true;

    if (this.parameters && !!this.settingsHolder.tool) {
      if (this.parameters.cutterDiameter) {
        const surfaceSpeed = parseFloat(
          this.conversionsFormulas.convertRpmToSfm(this.recommendation.rpm, this.getDiameterForSurfaceSpeed()).toString()
        );

        if (
          this.settingsHolder.currenttoolpath &&
          this.settingsHolder.currenttoolpath.operationName === 'Helical Plunge'
        ) {
          this.plungingParameters(undefined, this.recommendation.rpm, this.recommendation.ipm, surfaceSpeed);
        } else {
          this.millingParameters(
            undefined,
            this.recommendation.rpm,
            this.recommendation.ipm,
            surfaceSpeed,
            undefined,
            this.recommendation.trochoidalSlotWidth
          );
        }
      } else {
        this.plungingParameters(undefined, this.recommendation.rpm, this.recommendation.ipm, undefined);
      }
    }
  };

  /**
   * @param feedAggression The user-selected multiplier applied to feed
   */
  private updateFeedAggression = (feedAggression: number) => {
    this.feedAggression = feedAggression;
    this.recommendation.feedAggression = feedAggression;

    // Enable or disable the reset button
    this.resetFeedActive = feedAggression !== 1;

    // Recalculate based on updated aggression value
    this.recommendation.ipm = this.standardFeedValue * this.feedAggression;

    this.updateRecommendations.rpm = false;
    this.updateRecommendations.ipm = false;
    this.updateRecommendations.ipmAdjusted = true;
    this.updateRecommendations.rpmAdjusted = false;

    if (this.parameters && !!this.settingsHolder.tool) {
      const surfaceSpeed = parseFloat(
        this.conversionsFormulas.convertRpmToSfm(this.recommendation.rpm, this.getDiameterForSurfaceSpeed()).toString()
      );

      if (
        this.settingsHolder.currenttoolpath &&
        this.settingsHolder.currenttoolpath.operationName === 'Helical Plunge'
      ) {
        this.plungingParameters(undefined, this.recommendation.rpm, this.recommendation.ipm, undefined);
      } else {
        this.millingParameters(
          undefined,
          this.recommendation.rpm,
          this.recommendation.ipm,
          surfaceSpeed,
          undefined,
          this.recommendation.trochoidalSlotWidth
        );
      }
    }
  };

  pdfClick = $event => {
    let messageStr = 'Fill in Distributor Information and Click OK';
    forkJoin(this.cache.getItem('loginInfo')).subscribe(res => {
      let message: Message = {
        title: 'Please Enter Distributor Information',
        message: messageStr,
        user: res[0].firstName + ' ' + res[0].lastName,
        company: res[0].company
      };
      setTimeout(() => {
        this.utils.openDialog(message, 'pdfinfo');
      }, 500);
    });
  };

  makeid = () => {
    var text = '';
    var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (var i = 0; i < 5; i++) text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
  };

  savePDF = (doc: any, name: string) => {
    // does the saving of the PDF
    let pdfOutput = doc.output();
    let buffer = new ArrayBuffer(pdfOutput.length);
    let array = new Uint8Array(buffer);
    for (let i = 0; i < pdfOutput.length; i++) {
      array[i] = pdfOutput.charCodeAt(i);
    }

    let directory = '';

    if (!this.platform.is('ios') && !this.platform.is('android')) {
      //assume its web
      doc.save(name + '.pdf');
    } else {
      if (this.platform.is('ios')) {
        directory = this.file.documentsDirectory; // ios directory
      } else if (this.platform.is('android')) {
        directory = this.file.externalRootDirectory + '/Download/'; // android directory
      }
      //Name of pdf
      let fileName = name + '-' + this.makeid() + '.pdf';

      this.file
        .writeFile(directory, fileName, buffer)
        .then(success => {
          console.log('File created Succesfully' + JSON.stringify(success));
          this.fileOpener
            .open(directory + fileName, 'application/pdf')
            .then(() => console.log('File is opened'))
            .catch(e => console.log('Error openening file', e));
        })
        .catch(error => console.log('Cannot Create File ' + JSON.stringify(error)));
    }

    // Log data for user if user tries to save the PDF
    this.commonService.notifyOther({
      option: 'logdata',
      value: {
        logLocation: 'pdf'
      }
    });
  };

  generatePdf = () => {
    let doc = new jsPDF();

    doc.setFont('helvetica');

    // Generate Header, logos, set line dividers and set disclaimer
    this.generateHeaderAndLogos(doc);
    this.generatePdfDividers(doc);
    this.generateDisclaimer(doc);

    // Set doc font size, style and color and generate all section titles for the pdf
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 65, 112);
    this.generatePdfSectionTitles(doc);

    // Set doc font size, style and color for all of the information in the pdf
    // Utils append text function will handle if the color needs to be adjusted
    doc.setTextColor(0);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');

    const userToolPath: string = this.settingsHolder.currenttoolpath
      ? this.settingsHolder.currenttoolpath.operationName.toLowerCase()
      : '';

    this.generateUserInfo(doc);
    this.generateTool(doc);
    this.generateMaterial(doc);
    this.generateOperation(doc);
    this.generateMachine(doc);
    this.generateParameters(doc, userToolPath);
    this.generateRecommendations(doc, userToolPath);

    let name = this.settingsHolder.tool.itemId + '-' + this.settingsHolder.tool.description;
    if (this.isApp && this.platform.is('android')) {
      this.androidPermissions.checkPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
        result => {
          console.log('Has permission?', result.hasPermission);
          if (!result.hasPermission) {
            this.androidPermissions
              .requestPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE)
              .then(() => {
                // has no permission so, will ask for permission first
                this.savePDF(doc, name);
              });
          } else {
            this.savePDF(doc, name);
          }
        },
        err => this.androidPermissions.requestPermission(this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE)
      );
    } else {
      // web
      this.savePDF(doc, name);
    }

    if (this.isApp) {
      this.utils.apptentiveEngage('Save PDF');
    }
  };

  generateHeaderAndLogos = (doc: jsPDF) => {
    const brandKeySupportInfo = this.pdfSupportInfo.hasOwnProperty(this.settingsHolder.tool.brandKey)
      ? this.pdfSupportInfo[this.settingsHolder.tool.brandKey]
      : [];
    // Logo and Company Info
    doc.addImage(this.headerImage, 'png', 15, 22, 69, 17);
    doc.addImage(this.helicalImg, 'png', 131, 284, 23, 3);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 65, 112);
    doc.text('www.machiningadvisorpro.com', 15, 43);
    doc.text('Application Summary Report', 100, 25);
    doc.setTextColor(0);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Date:', 100, 30);
    let date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(date, 135, 30);
    let infyYPostion = 37;
    brandKeySupportInfo.forEach(info => {
      doc.text(info.supportName + ':', 100, infyYPostion);
      doc.text(info.supportPhoneNumber + ' | ' + info.supportEmail, 135, infyYPostion);
      infyYPostion += 5;
    });
  };

  generatePdfSectionTitles = (doc: jsPDF) => {
    doc.text('END USER', this.xTitleLeft, 68);
    doc.text('DISTRIBUTOR', this.xTitleRight, 68);
    doc.text('TOOL', this.xTitleLeft, 97);
    doc.text('MATERIAL', this.xTitleRight, 97);
    doc.text('OPERATION', this.xTitleRight, 136);
    doc.text('MACHINE', this.xTitleRight, 164);
    doc.text('PARAMETERS', this.xTitleLeft, 213);
    doc.text('RECOMMENDATIONS', this.xTitleRight, 213);
  };

  generatePdfDividers = (doc: jsPDF) => {
    // Set line dividers for pdf
    doc.setLineWidth(1);
    doc.setDrawColor(37, 65, 112);
    doc.line(15, 89, 195, 89);
    doc.line(15, 206, 195, 206);
  };

  generateUserInfo = (doc: jsPDF) => {
    let userTitlesTitles = ['End User Name:', 'End User Company:'];

    let userTitlesInfo = [this.endUser, this.endUserCompany];

    let distributorTitles = ['Distributor Name:', 'Distributor Company:'];

    let distributorInfo = [this.distName, this.distCompany];

    this.utils.listPdfText(doc, userTitlesTitles, this.xTitleLeft, 75, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, distributorTitles, this.xTitleRight, 75, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, userTitlesInfo, this.xInfoLeft, 75, this.maxLineWidth, 'info');
    this.utils.listPdfText(doc, distributorInfo, this.xInfoRight, 75, this.maxLineWidth, 'info');
  };

  generateTool = (doc: jsPDF) => {
    const toolTitles = [];
    const toolInfo = [];
    let toolUrl = '';
    let toolNumber = '';
    this.settingsHolder.tool.toolDetails.forEach(td => {
      toolTitles.push(td.itemName);
      toolInfo.push(td.itemValue);

      // Only item with a url is the tool #. Getting it here to build out the tool url
      if (td.itemName.includes('Tool #')) {
        toolUrl = td.itemUrl;
        toolNumber = td.itemName;
      }
    });

    this.utils.listPdfText(doc, toolTitles, this.xTitleLeft, 104, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, toolInfo, this.xInfoLeft, 104, this.maxLineWidth, 'info');

    /* getStringUnitWidth returns string width as 1pt font size. To get the width for the font size we are using
    we need to multiple that result by 8. We are using default units for the pdf, mm, so we need to convert the length
    to that by divinding by 72/25.6 to get mm*/
    doc.setTextColor(0);
    doc.setLineWidth(0.25);
    doc.setDrawColor(0, 0, 0);
    const lineArray: [] = doc.splitTextToSize(toolNumber, this.maxLineWidth);
    let lineUnderlineYCord = 111.5;
    lineArray.forEach(line => {
      const lineDimension = (doc.getStringUnitWidth(line) * 8) / (72 / 25.6);
      doc.line(this.xInfoLeft, lineUnderlineYCord, this.xInfoLeft + lineDimension, lineUnderlineYCord);
      doc.link(this.xInfoLeft, lineUnderlineYCord - 4, lineDimension, 5, { url: toolUrl });
      lineUnderlineYCord += 3;
    });
  };

  generateMaterial = (doc: jsPDF) => {
    let materialTitles = ['Material:', 'Subgroup:', 'Condition:', 'Hardness:'];

    let materialInfo = [
      this.settingsHolder.material.subGroup,
      this.settingsHolder.material.name + ' (UNS ' + this.settingsHolder.material.uns + ')',
      this.settingsHolder.material.condition,
      Math.round(this.settingsHolder.material.hrc) +
        ' HRC (' +
        this.settingsHolder.material.hardness +
        ' ' +
        this.settingsHolder.material.hardnessFactor +
        ')'
    ];

    this.utils.listPdfText(doc, materialTitles, this.xTitleRight, 104, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, materialInfo, this.xInfoRight, 104, this.maxLineWidth, 'info');
  };

  generateOperation = (doc: jsPDF) => {
    let operationTitles = ['Type:', 'Tool Path:'];
    let operationInfo = [
      this.settingsHolder.selectedOperation || '',
      this.settingsHolder.currenttoolpath ? this.settingsHolder.currenttoolpath.operationName : ''
    ];

    if (this.settingsHolder.hasStrategyOption && this.settingsHolder.currentStrategy) {
      operationTitles.push('Strategy:');
      operationInfo.push(this.settingsHolder.currentStrategy.operationName || '');
    }

    this.utils.listPdfText(doc, operationTitles, this.xTitleRight, 143, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, operationInfo, this.xInfoRight, 143, this.maxLineWidth, 'info');
  };

  generateMachine = (doc: jsPDF) => {
    let machineTitles = ['Spindle:', 'Holder:', 'Max RPM:', 'Max IPM:', 'Workholding Rigidity:'];

    let maxRpm = this.settingsHolder.machine.maxRPM ? this.settingsHolder.machine.maxRPM : 'N/A';
    let maxIpm = this.settingsHolder.machine.maxIPM ? this.settingsHolder.machine.maxIPM : 'N/A';

    let machineInfo = [
      this.settingsHolder.machine.spindle,
      this.settingsHolder.machine.holder,
      maxRpm,
      maxIpm,
      this.settingsHolder.machine.fixtureBar + '%'
    ];

    this.utils.listPdfText(doc, machineTitles, this.xTitleRight, 171, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, machineInfo, this.xInfoRight, 171, this.maxLineWidth, 'info');
  };

  generateParameters = (doc: jsPDF, userToolPath: string) => {
    let parameterTitles = [];
    let parameterInfo = [];
    let parameterHyperlink = [];
    if (this.mapMode === 1) {
      switch (userToolPath) {
        case 'chamfering':
          parameterTitles = ['Stick Out:', 'Effective Diameter:', 'Chamfer Height:', 'Chamfer Width:'];
          parameterInfo = [
            this.settingsHolder.parameters.stickOut,
            this.settingsHolder.parameters.effectiveDiameter,
            this.settingsHolder.parameters.chamferHeight,
            this.settingsHolder.parameters.chamferWidth
          ];
          break;
        case '3d surfacing':
          let muOffset =
            0.5 +
            this.xInfoLeft +
            (doc.getStringUnitWidth(this.settingsHolder.parameters.scallopHeight.toString()) * doc.getFontSize()) /
              doc.internal.scaleFactor;
          doc.addImage(this.muSymbol, 'png', muOffset, 231.2, 3.5, 3.5);
          parameterTitles = ['Stick Out:', 'Axial Depth:', 'Scallop Height (Ra):', 'Tool Stepover:'];
          parameterInfo = [
            this.settingsHolder.parameters.stickOut,
            this.settingsHolder.parameters.adocdecimal +
              ' (' +
              this.settingsHolder.parameters.stepDown +
              '% step down)',
            this.settingsHolder.parameters.scallopHeight + '     in',
            this.settingsHolder.parameters.scallopWidth
          ];
          break;
        case 'helical plunge':
          parameterTitles = ['Stick Out:', 'Ramp Angle:', 'Path Diameter:', 'Hole Depth:'];
          parameterInfo = [
            this.settingsHolder.parameters.stickOut,
            this.settingsHolder.parameters.rampAngle,
            this.settingsHolder.parameters.pathDiameter,
            this.settingsHolder.parameters.holeDepth
          ];
          break;
        default:
          parameterTitles = ['Stick Out:', 'Axial Depth:', 'Radial Depth:', 'Engagement Angle:'];
          parameterInfo = [
            this.settingsHolder.parameters.stickOut,
            this.settingsHolder.parameters.adocdecimal +
              ' (' +
              this.settingsHolder.parameters.stepDown +
              '% step down)',
            this.settingsHolder.parameters.rdocdecimal +
              ' (' +
              this.settingsHolder.parameters.stepOver +
              '% step over)',
            this.settingsHolder.parameters.engagementAngle + '°'
          ];
          break;
      }
    } else if (this.mapMode === 2) {
      const mapModeTwoParams: ParameterLabel[] = this.settingsHolder.mapModeTwoValues
        ? this.settingsHolder.mapModeTwoValues.parameterLabels
        : [];
      mapModeTwoParams.forEach(par => {
        parameterTitles.push(par.parameterLabel + ':');
        parameterInfo.push(par.parameterValue);
        parameterHyperlink.push(par.parameterHyperlink);
      });
    }

    this.utils.listPdfText(doc, parameterTitles, this.xTitleLeft, 220, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, parameterInfo, this.xInfoLeft, 220, this.maxLineWidth, 'info', parameterHyperlink);
  };

  generateRecommendations = (doc: jsPDF, userToolPath: string) => {
    let chipThickness = this.recommendation.chipThickness ? this.recommendation.chipThickness.toFixed(4) : '0';
    let recommendationsTitles = [];
    let recommendationsInfo = [];
    if (this.mapMode === 1) {
      switch (userToolPath) {
        case 'chamfering':
          recommendationsTitles = ['Surface Speed:', 'Speed:', 'Feed / Tooth:', 'Feed / Minute:'];
          recommendationsInfo = [
            math.round(this.recommendation.sfm, 0),
            math.round(this.recommendation.rpm, 0),
            math.round(this.recommendation.ipt, 4),
            math.round(this.recommendation.ipm, 0)
          ];
          break;
        case 'trochoidal slotting':
          recommendationsTitles = [
            'Trochoidal Slot Width:',
            'Surface Speed:',
            'Speed:',
            'Feed / Minute:',
            'Chip Thickness:',
            'MRR:'
          ];
          recommendationsInfo = [
            math.round(this.recommendation.trochoidalSlotWidth, 4),
            math.round(this.recommendation.sfm, 0),
            math.round(this.recommendation.rpm, 0),
            math.round(this.recommendation.ipmFeedInsideSlot, 0),
            chipThickness,
            math.round(this.recommendation.mrr, 3)
          ];
          break;
        case 'helical plunge':
          recommendationsTitles = ['Surface Speed:', 'Speed:', 'Feed / Tooth:', 'Feed / Minute:', 'MRR:'];
          recommendationsInfo = [
            math.round(this.recommendation.sfm, 0),
            math.round(this.recommendation.rpm, 0),
            math.round(this.recommendation.ipt, 4),
            math.round(this.recommendation.ipm, 0),
            math.round(this.recommendation.mrr, 3)
          ];
          break;
        case '3d surfacing':
          recommendationsTitles = ['Surface Speed:', 'Speed:', 'Feed / Tooth:', 'Feed / Minute:', 'Chip Thickness:'];
          recommendationsInfo = [
            math.round(this.recommendation.sfm, 0),
            math.round(this.recommendation.rpm, 0),
            math.round(this.recommendation.ipt, 4),
            math.round(this.recommendation.ipm, 0),
            chipThickness
          ];
          break;
        default:
          recommendationsTitles = [
            'Surface Speed:',
            'Speed:',
            'Feed / Tooth:',
            'Feed / Minute:',
            'Chip Thickness:',
            'MRR:'
          ];
          recommendationsInfo = [
            math.round(this.recommendation.sfm, 0),
            math.round(this.recommendation.rpm, 0),
            math.round(this.recommendation.ipt, 4),
            math.round(this.recommendation.ipm, 0),
            chipThickness,
            math.round(this.recommendation.mrr, 3)
          ];
          break;
      }
    } else if (this.mapMode === 2) {
      const mapModeTwoRecs: RecommendationLabel[] = this.settingsHolder.mapModeTwoValues
        ? this.settingsHolder.mapModeTwoValues.recommendationLabels
        : [];
      mapModeTwoRecs.forEach(rec => {
        recommendationsTitles.push(rec.recommendationLabel + ':');
        recommendationsInfo.push(
          math.round(this.calculateRecommendationValue(rec), Number(rec.recommendationPrecision))
        );
      });
    }

    this.utils.listPdfText(doc, recommendationsTitles, this.xTitleRight, 220, this.maxLineWidth, 'titles');
    this.utils.listPdfText(doc, recommendationsInfo, this.xInfoRight, 220, this.maxLineWidth, 'info');
  };

  generateDisclaimer = doc => {
    doc.setFontSize(7);
    doc.setTextColor(100);
    let disclaimer = 'NOTE: This application and the results it formulates are theoretical in nature. ';
    disclaimer += 'Harvey Performance Company, LLC assumes no responsibility. ';
    disclaimer += '© Copyright 2018 Harvey Performance Company, LLC.';
    doc.text(doc.splitTextToSize(disclaimer, 115), 15, 285);
  };

  onResize = () => {};

  emitData = (specificChild: string = undefined, origin: string = undefined, emit: boolean = true) => {
    let data = {
      recommendation: this.recommendation,
      recommendationForm: this.Recommendations
    };

    if (!!!specificChild) {
      this.recommendationChange.emit(data);
    } else {
      this.recommendationChangeWithChild.emit({
        specificChild: specificChild,
        origin: origin,
        data: data,
        emit: emit
      });
    }
  };

  resetSpeedSlider = () => {
    this.Recommendations.get('speedSlider').patchValue(this.sliderConfig.start, { emitEvent: true });
  };

  resetFeedSlider = () => {
    this.Recommendations.get('feedSlider').patchValue(this.sliderConfig.start, { emitEvent: true });
  };

  viewGCode = () => {
    this.utils.openDialog(
      {
        title: 'G-Code',
        message: '',
        data: {
          ipm: this.recommendation.ipm,
          pitch: this.settingsHolder.parameters.pitch,
          holeDepth: this.settingsHolder.parameters.holeDepth,
          pathRadius: this.settingsHolder.parameters.pathDiameter
        }
      },
      'gcode'
    );
    if (this.isApp) {
      this.utils.apptentiveEngage('View G-Code');
    }
  };

  ngAfterViewInit() {
    setTimeout(() => {
      this.onResize();

      this.Recommendations.get('speedSlider').valueChanges.subscribe((speed: number) => {
        this.updateSpeedAggression(speed / 100);
        this.Recommendations.get('speedInput').patchValue(speed, { emitEvent: false });
        if (this.isApp) {
          this.utils.apptentiveEngage('Speed (Slider)');
        }

        if (
          this.mapMode === 2 &&
          this.machine.holderAdjustment &&
          this.machine.spindleAdjustment &&
          this.settingsHolder.mapModeTwoValues &&
          this.settingsHolder.mapModeTwoValues.recommendationLabels
        ) {
          const selectedHolderAdjustment = this.machine.holderAdjustment.find(ha => ha.adjust === 'SFM');
          const selectedSpindleAdjustment = this.machine.spindleAdjustment.find(sa => sa.adjust === 'FPT');

          this.sfmRation = this.formulas.calculateSfmRatio(
            this.settingsHolder.mapModeTwoValues.recommendationLabels,
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            this.machine.maxRPM,
            this.speedAggression,
            this.feedAggression
          );
          this.fptRation = this.formulas.calculateFptRatio(
            this.settingsHolder.mapModeTwoValues.recommendationLabels,
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            this.machine.maxIPM,
            this.speedAggression,
            this.feedAggression,
            this.sfmRation
          );
        }
        this.updateRecommendationsForDataLogging();
      });

      this.Recommendations.get('feedSlider').valueChanges.subscribe((feed: number) => {
        this.updateFeedAggression(feed / 100);
        this.Recommendations.get('feedInput').patchValue(feed, { emitEvent: false });
        if (this.isApp) {
          this.utils.apptentiveEngage('Feed (Slider)');
        }

        if (
          this.mapMode === 2 &&
          this.machine.holderAdjustment &&
          this.machine.spindleAdjustment &&
          this.settingsHolder.mapModeTwoValues &&
          this.settingsHolder.mapModeTwoValues.recommendationLabels
        ) {
          const selectedHolderAdjustment = this.machine.holderAdjustment.find(ha => ha.adjust === 'SFM');
          const selectedSpindleAdjustment = this.machine.spindleAdjustment.find(sa => sa.adjust === 'FPT');

          this.sfmRation = this.formulas.calculateSfmRatio(
            this.settingsHolder.mapModeTwoValues.recommendationLabels,
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            this.machine.maxRPM,
            this.speedAggression,
            this.feedAggression
          );
          this.fptRation = this.formulas.calculateFptRatio(
            this.settingsHolder.mapModeTwoValues.recommendationLabels,
            selectedHolderAdjustment.factor,
            selectedSpindleAdjustment.factor,
            this.machine.maxIPM,
            this.speedAggression,
            this.feedAggression,
            this.sfmRation
          );
        }
        this.updateRecommendationsForDataLogging();
      });

      this.Recommendations.get('type2')
        .get('trochoidalSlotWidth')
        .valueChanges.pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((value: number) => {
          if (_.trim(value.toString()) !== '') {
            this.Recommendations.get('type2')
              .get('trochoidalSlotWidth')
              .patchValue(math.round(value, 4), { emitEvent: false });
            this.recommendation.trochoidalSlotWidth = math.round(value, 4);
            // need to emit to Parameters have it recalc, BUT don't update recommendation when it comes back
            // lets emitData directly to parameters, no need to tell any other widget
            this.commonService.notifyOther({
              option: 'trochoidalSlotWidthChange',
              value: this.settingsHolder
            }); // resetting all, all will listen for a "RESET"
          }
          if (this.isApp) {
            this.utils.apptentiveEngage('Trochoidal Slot Width');
          }
          this.updateRecommendationsForDataLogging();
        });

      this.subscription = this.commonService.notifyObservable$.subscribe(res => {
        if (res.hasOwnProperty('option') && res.option === 'logout') {
          this.subscription.unsubscribe();
        }

        if (res.hasOwnProperty('option') && res.option === 'dialog') {
          if (res.value) {
            this.distName = res.value.distName;
            this.distCompany = res.value.distCompany;
            this.endUser = res.value.endUserName;
            this.endUserCompany = res.value.endUserCompany;
            this.generatePdf();
          }
        }

        if ((res.hasOwnProperty('option') && res.option == 'mapModeChanged') || res.option === 'clearToolPath') {
          if (this.settingsHolder) {
            this.mapMode = this.settingsHolder.mapMode;
          }
        }

        if (res.hasOwnProperty('option') && res.option === 'resetInsideCornerRadiusOfWall') {
          this.resetInsideCornerRadiusOfWall();
        }

        if (res.hasOwnProperty('option') && res.option === 'recommendations') {
          if (res.value.machine.hasOwnProperty('holderAdjustment') && !!res.value.machine.holderAdjustment) {
            const selectedHolderFactor = res.value.machine.holderAdjustment.find(ha => ha.adjust === 'Speed');
            const selectedHolderFeedFactor = res.value.machine.holderAdjustment.find(ha => ha.adjust === 'Feed');
            this.holderFactor = selectedHolderFactor ? selectedHolderFactor.factor : 0;
            this.holderFeedFactor = selectedHolderFeedFactor ? selectedHolderFeedFactor.factor : 0;
          }
          if (res.value.machine.hasOwnProperty('spindleAdjustment') && !!res.value.machine.spindleAdjustment) {
            const selectedSpindleFactor = res.value.machine.spindleAdjustment.find(sa => sa.adjust === 'Feed');
            this.spindleFactor = selectedSpindleFactor ? selectedSpindleFactor.factor : 0;
          }
          this.machineTabFilledIn = !!res.value.machine && !!res.value.machine.spindle && !!res.value.machine.holder;
        }

        if ((res.hasOwnProperty('option') && res.option === 'mapModeChanged') || res.option === 'clearToolPath') {
          this.currentMapMode = res.value.mapMode;
        }

        if (
          res.hasOwnProperty('option') &&
          (res.option === 'recommendations' ||
            res.option === 'parameterReportingToRecoomendation' ||
            res.option === 'trochoidalSlotWidthChangeBackToRecommendations')
        ) {
          if (res.value) {
            setTimeout(() => {
              if (res.origin !== 'recommendations') {
                const trochoidalSlotWidthChange = res.option === 'trochoidalSlotWidthChangeBackToRecommendations';

                //had to wrap it in a setTimeout, recommendations was being millingparameters was being called before parameters finished calc
                this.warningAlerts = res.warningAlerts;
                this.settingsHolder = res.value;
                // need to let parameters ONLY which recommendation field
                // only emit IF previous Recommendation field is different from current
                if (this.settingsHolder && this.warningAlerts) {
                  if (this.settingsHolder.machine) {
                    if (this.fixture !== this.settingsHolder.machine.fixtureBar) {
                      this.fixture = this.settingsHolder.machine.fixtureBar;
                      this.fixtureChange = true;
                    }

                    if (this.machine !== this.settingsHolder.machine) {
                      this.machine = this.settingsHolder.machine;
                    }
                  }
                }

                // we need to look for it with the string
                if (this.settingsHolder.currenttoolpath || this.fixtureChange) {
                  this.currenttoolpath = this.settingsHolder.currenttoolpath
                    ? this.settingsHolder.currenttoolpath.operationName
                    : '';
                  this.recommendationAdjustment = this.settingsHolder.recommendationFields;
                  //set the html so we need to give it some time
                  if (this.currenttoolpath === 'Helical Plunge') {
                    this.updateRecommendations.rpm = true;
                    this.updateRecommendations.ipm = true;
                    this.recommendation = {};
                    this.plungingParameters(res.emit);
                    this.standardSpeedValue = this.recommendation.rpm;
                    this.standardFeedValue = this.recommendation.ipm;
                  } else {
                    this.updateRecommendations.rpm = true;
                    this.updateRecommendations.ipm = true;
                    this.recommendation = {};

                    let trochoidalSlotWidth: number = undefined;
                    if (this.currenttoolpath === 'Trochoidal Slotting') {
                      if (trochoidalSlotWidthChange) {
                        trochoidalSlotWidth = math.round(
                          this.settingsHolder.recommendationForm.get('type2').get('trochoidalSlotWidth').value,
                          4
                        );
                      } else if (!res.workHoldingChange) {
                        trochoidalSlotWidth = math.round(this.settingsHolder.tool.cutterDiameter * 1.5, 4);
                        this.Recommendations.get('type2')
                          .get('trochoidalSlotWidth')
                          .patchValue(trochoidalSlotWidth, { emitEvent: false });
                      }
                    }

                    this.millingParameters(res.emit, undefined, undefined, undefined, undefined, trochoidalSlotWidth);
                    this.standardSpeedValue = this.recommendation.rpm;
                    this.standardFeedValue = this.recommendation.ipm;
                  }

                  if (this.recommendationAdjustment === 2 && !trochoidalSlotWidthChange) {
                    //need to notify parameters
                    this.commonService.notifyOther({
                      option: 'FeedInsideSlotChange',
                      value: this.Recommendations
                    });
                  }

                  //reset the fixture after
                  this.fixtureChange = false;
                }

                if (
                  this.machine.holderAdjustment &&
                  this.machine.spindleAdjustment &&
                  this.settingsHolder.displayParamsAndRecs &&
                  this.settingsHolder.mapMode === 2 &&
                  this.settingsHolder.mapModeTwoValues &&
                  this.settingsHolder.mapModeTwoValues.recommendationLabels
                ) {
                  const selectedHolderAdjustment = this.machine.holderAdjustment.find(ha => ha.adjust === 'SFM');
                  const selectedSpindleAdjustment = this.machine.spindleAdjustment.find(sa => sa.adjust === 'FPT');

                  this.sfmRation = this.formulas.calculateSfmRatio(
                    this.settingsHolder.mapModeTwoValues.recommendationLabels,
                    selectedHolderAdjustment.factor,
                    selectedSpindleAdjustment.factor,
                    this.machine.maxRPM,
                    this.speedAggression,
                    this.feedAggression
                  );
                  this.fptRation = this.formulas.calculateFptRatio(
                    this.settingsHolder.mapModeTwoValues.recommendationLabels,
                    selectedHolderAdjustment.factor,
                    selectedSpindleAdjustment.factor,
                    this.machine.maxIPM,
                    this.speedAggression,
                    this.feedAggression,
                    this.sfmRation
                  );
                }
              }

              if (
                this.settingsHolder.material &&
                this.settingsHolder.material.chosenCondition &&
                this.settingsHolder.machine.holder &&
                this.settingsHolder.currenttoolpath &&
                this.settingsHolder.currenttoolpath.operationName &&
                (!this.settingsHolder.hasStrategyOption ||
                  (this.settingsHolder.currentStrategy && this.settingsHolder.currentStrategy.operationName)) &&
                this.settingsHolder.tool &&
                (this.mapMode === 1 || (this.mapMode === 2 && this.settingsHolder.mapModeTwoValues))
              ) {
                this.renderRecommendations = true;
              } else {
                this.renderRecommendations = false;
              }
              this.updateRecommendationsForDataLogging();
            });
          }
        } else if (
          res.hasOwnProperty('option') &&
          (res.option === 'reset' ||
            res.option === 'resetTool' ||
            res.option === 'switchedFromChamferingTool' ||
            res.option === 'resetMaterial')
        ) {
          this.currentMapMode = 1;
          this.machineTabFilledIn = false;
          this.settingsHolder = new ToolContainer();
          this.fixtureChange = false;
          this.machine = new Machine_();
          this.fixture = 0;
          this.Recommendations.reset({}, { emitEvent: false });
          this.renderRecommendations = false;
          this.warningMessageOpen = false;
          this.recommendation = {};
          this.resetSpeedSlider();
          this.resetFeedSlider();
          this.mapMode = this.settingsHolder.mapMode;
          if (this.isApp) {
            // emit to summary page
            this.commonService.notifyOther({
              option: 'summaryAlert',
              value: {
                settingsHolder: undefined,
                recommendation: undefined
              }
            });
          }
        }
      });
    });
  }

  updateRecommendationsForDataLogging = () => {
    this.commonService.notifyOther({
      option: 'updateRecommendationsDataLog',
      value: this.recommendation
    });
  };

  speedSliderClick = (event: MouseEvent) => {
    const target: Element = event.target as Element;
    if (target.classList.contains('noUi-value')) {
      const value = parseInt(target.attributes.getNamedItem('data-value').value);

      this.updateSpeedAggression(value / 100);
      this.Recommendations.get('speedSlider').patchValue(value, { emitEvent: false });
      this.Recommendations.get('speedInput').patchValue(value, { emitEvent: false });
    }
  };

  feedSliderClick = (event: MouseEvent) => {
    const target: Element = event.target as Element;
    if (target.classList.contains('noUi-value')) {
      const value = parseInt(target.attributes.getNamedItem('data-value').value);

      this.updateFeedAggression(value / 100);
      this.Recommendations.get('feedSlider').patchValue(value, { emitEvent: false });
      this.Recommendations.get('feedInput').patchValue(value, { emitEvent: false });
    }
  };

  restrictInputValue = (input: AbstractControl, value: number, min: number, max: number) => {
    if (value > max || value < min) {
      value = Math.min(Math.max(value, min), max);
      input.patchValue(value, { emitEvent: false });
    }
  };

  updateSpeedAggressionInput = (speed: number) => {
    const input: AbstractControl = this.Recommendations.get('speedInput');
    this.restrictInputValue(input, speed, this.sliderConfig.range.min, this.sliderConfig.range.max);

    this.updateSpeedAggression(speed / 100);
    this.Recommendations.get('speedSlider').patchValue(speed, { emitEvent: false });
  };

  updateFeedAggressionInput = (feed: number) => {
    const input: AbstractControl = this.Recommendations.get('feedInput');
    this.restrictInputValue(input, feed, this.sliderConfig.range.min, this.sliderConfig.range.max);

    this.updateFeedAggression(feed / 100);
    this.Recommendations.get('feedSlider').patchValue(feed, { emitEvent: false });
  };

  updateInsideCornerRadiusInput = (input: string) => {
    const insideCornerRadius = Number(input);
    if (!insideCornerRadius) {
      // this acts as a reset, mimicing original app, no calculations should be made, instead, reset the fields
      this.resetInsideCornerRadiusOfWall();
    } else {
      this.recommendation.insideCornerValue = insideCornerRadius;
    }
    this.emitData('parameters', 'recommendations', false);
    this.millingParameters(false);
    this.commonService.notifyOther({
      option: 'insideCornerChange',
      value: this.settingsHolder
    });
    if (this.isApp) {
      this.utils.apptentiveEngage('Inside Corner Radius');
    }
  };

  resetInsideCornerRadiusOfWall = () => {
    this.Recommendations.get('type1')
      .get('insideCornerRadiusOfWall')
      .reset({ value: '' });
    this.recommendation.insideCornerValue = undefined;
  };

  setRecommendation = (selectedCornerType: string) => {
    this.insideCornerType = selectedCornerType;
    this.millingParameters(false);
    this.commonService.notifyOther({
      option: 'insideCornerChange',
      value: this.settingsHolder
    });
  };

  millingParameters = (
    emit: boolean = true,
    rpm?: number,
    ipm?: number,
    surfaceSpeed?: number,
    feedFactor?: number,
    trochoidalSlotWidth?: number
  ) => {
    if (
      this.settingsHolder.parameters &&
      this.settingsHolder.machine &&
      this.settingsHolder.material &&
      this.settingsHolder.tool &&
      this.settingsHolder.currenttoolpath &&
      this.settingsHolder.currenttoolpath.operationName &&
      (!this.settingsHolder.hasStrategyOption ||
        (this.settingsHolder.currentStrategy && this.settingsHolder.currentStrategy.operationName))
    ) {
      this.parameters = _.clone(this.settingsHolder.parameters);
      this.parameters.toolPath = this.settingsHolder.hasStrategyOption
        ? this.settingsHolder.currentStrategy
        : this.settingsHolder.currenttoolpath;
      if (this.settingsHolder.currenttoolpath.operationName === 'Chamfering') {
        this.parameters.cutDepth = this.settingsHolder.parameters.chamferHeight;
        this.parameters.cutWidth = this.settingsHolder.parameters.chamferWidth;
        this.parameters.cutterDiameter = this.settingsHolder.parameters.effectiveDiameter;
      } else if (this.settingsHolder.currenttoolpath.operationName === '3D Surfacing') {
        let cutDepth = parseFloat(this.settingsHolder.parameters.adocdecimal + '');
        cutDepth = cutDepth ? cutDepth : 0;

        this.parameters.cutDepth = cutDepth;
        this.parameters.cutterDiameter = this.formulas.calculateEffectiveDiameter(
          this.settingsHolder.tool.cutterDiameter,
          this.parameters.cutDepth
        );

        let scallopWidth = parseFloat(this.settingsHolder.parameters.scallopWidth + '');

        scallopWidth = scallopWidth ? scallopWidth : 0;

        this.parameters.scallopWidth = scallopWidth; // not sure why scallopwidth is being passed over as a string
        this.parameters.cutWidth = (this.parameters.scallopWidth + this.parameters.cutterDiameter) / 2;
      } else {
        this.parameters.cutDepth = this.settingsHolder.parameters.adocdecimal;
        this.parameters.cutWidth = this.settingsHolder.parameters.rdocdecimal;
      }

      let hra = this.settingsHolder.hardness.userHra ? this.settingsHolder.hardness.userHra : 0;

      let millValues = this.formulas.calculateStandardMachining(
        this.settingsHolder.material,
        this.settingsHolder.tool,
        this.parameters,
        hra
      );

      surfaceSpeed =
        millValues.surfaceSpeed *
        this.setupFactor('Speed', this.settingsHolder.material, this.settingsHolder.machine.fixtureBar);

      this.spindleSpeed = this.setSpeed(this.parameters.cutterDiameter, surfaceSpeed);
      let chipLoad =
        (millValues.chipLoad *
          this.setupFactor('Feed', this.settingsHolder.material, this.settingsHolder.machine.fixtureBar) *
          this.parameters.cutterDiameter) /
        this.settingsHolder.tool.cutterDiameter;

      let feedFactor = millValues.feedFactor;
      if (this.updateRecommendations.ipm || !!!this.recommendation.ipm || this.fixtureChange || rpm || ipm) {
        this.feedRate = this.setFeed(
          this.settingsHolder.tool.fluteCount,
          chipLoad,
          this.setSpeed(this.parameters.cutterDiameter, surfaceSpeed, 'feed'),
          feedFactor
        );
      } else {
        this.feedRate = this.recommendation.ipm;
      }

      if (_.trim(this.settingsHolder.machine.maxIPM).length > 0 && this.settingsHolder.machine.maxIPM < this.feedRate) {
        this.feedRate = this.settingsHolder.machine.maxIPM;
      }

      let feedRate = this.feedRate;
      let feedPerTooth = this.conversionsFormulas.convertIpmToIpt(
        this.spindleSpeed,
        this.feedRate,
        this.settingsHolder.tool.fluteCount
      );

      let chipThickness;
      let mrr = 0;

      if (
        this.settingsHolder.currenttoolpath.operationName !== '3D Surfacing' &&
        this.settingsHolder.currenttoolpath.operationName !== 'Chamfering'
      ) {
        mrr = this.parameters.cutDepth * this.parameters.cutWidth * this.feedRate;
      }

      if (!!!this.recommendation.chipThickness || this.updateRecommendations.ipmAdjusted || this.fixtureChange) {
        if (this.settingsHolder.currenttoolpath.operationName === '3D Surfacing') {
          chipThickness = (feedPerTooth * this.parameters.cutterDiameter) / this.settingsHolder.tool.cutterDiameter;
        } else if (
          this.settingsHolder.currenttoolpath.operationName !== '3D Surfacing' &&
          this.settingsHolder.currenttoolpath.operationName !== 'Chamfering'
        ) {
          chipThickness = feedPerTooth / feedFactor;
        }
      } else {
        chipThickness = this.recommendation.chipThickness;
      }

      if (this.updateRecommendations.ipm) {
        this.updateRecommendations.ipm = false;
      }

      if (this.recommendationAdjustment === 2 || this.recommendationAdjustment === 1) {
        this.filletFeed(feedRate);
      }

      this.updateRecommendations.rpmAdjusted = false;
      this.updateRecommendations.ipmAdjusted = false;

      //feed adjustments, IPT, IPM, mrr and chipthickness gets affected

      this.recommendation = {
        ...this.recommendation,
        rpm: this.spindleSpeed,
        ipm: this.feedRate,
        sfm: parseFloat(
          this.conversionsFormulas
            .convertRpmToSfm(this.spindleSpeed, this.getDiameterForSurfaceSpeed())
            .toString()
        ),
        ipt: feedPerTooth,
        mrr: mrr,
        chipThickness: chipThickness,
        trochoidalSlotWidth: trochoidalSlotWidth,
        speedAggression: this.speedAggression,
        feedAggression: this.feedAggression
      };

      if (!this.warningMessageOpen) {
        this.maxCutDiaMessage(millValues.baseMRR);
      }

      if (this.isApp) {
        // emit to summary page
        this.commonService.notifyOther({
          option: 'summaryAlert',
          value: {
            settingsHolder: this.settingsHolder,
            recommendation: this.recommendation
          }
        });
      }
    }
  };

  public calculateRecommendationValue = (recommendation: RecommendationLabel): number => {
    const selectedHolderAdjustment = this.machine.holderAdjustment
      ? this.machine.holderAdjustment.find(ha => ha.adjust === 'SFM')
      : null;
    const selectedSpindleAdjustment = this.machine.spindleAdjustment
      ? this.machine.spindleAdjustment.find(sa => sa.adjust === 'FPT')
      : null;

    const recValue =
      Number(recommendation.recommendationValue) *
      (recommendation.recommendationAdjustment.includes('SFM')
        ? (selectedHolderAdjustment ? selectedHolderAdjustment.factor : 0) * this.speedAggression * this.sfmRation
        : 1) *
      (recommendation.recommendationAdjustment.includes('FPT')
        ? (selectedSpindleAdjustment ? selectedSpindleAdjustment.factor : 0) * this.feedAggression * this.fptRation
        : 1);

    return math.round(recValue, Number(recommendation.recommendationPrecision));
  };

  plungingParameters = (emit: boolean = true, rpm?: number, ipm?: number, surfaceSpeed?: number) => {
    if (
      this.settingsHolder.parameters ||
      (this.settingsHolder.machine &&
        this.settingsHolder.material &&
        this.settingsHolder.tool &&
        this.settingsHolder.currenttoolpath)
    ) {
      this.parameters = _.clone(this.settingsHolder.parameters);

      if (
        this.parameters &&
        this.settingsHolder.material &&
        this.settingsHolder.tool &&
        this.settingsHolder.currenttoolpath
      ) {
        let holeDiameter = this.parameters.holeDiameter;
        let pitch = this.parameters.pitch;
        let holeDepth = this.parameters.holeDepth;

        if (
          this.settingsHolder.material &&
          this.settingsHolder.machine &&
          this.settingsHolder.currenttoolpath &&
          this.settingsHolder.tool
        ) {
          let millValues = this.formulas.calculatePlungingMachining(
            this.settingsHolder.material,
            this.settingsHolder.tool,
            this.parameters,
            this.settingsHolder.hardness.hra
          );

          surfaceSpeed =
            millValues.surfaceSpeed *
            this.setupFactor('Speed', this.settingsHolder.material, this.settingsHolder.machine.fixtureBar);

          let chipLoad =
            millValues.chipLoad *
            this.setupFactor('Feed', this.settingsHolder.material, this.settingsHolder.machine.fixtureBar);

          this.spindleSpeed = this.setSpeed(this.parameters.cutterDiameter, surfaceSpeed);

          if (
            _.trim(this.settingsHolder.machine.maxRPM).length > 0 &&
            this.settingsHolder.machine.maxRPM < this.spindleSpeed
          ) {
            this.spindleSpeed = this.settingsHolder.machine.maxRPM;
            surfaceSpeed = this.conversionsFormulas.convertRpmToSfm(
              this.spindleSpeed,
              this.getDiameterForSurfaceSpeed()
            );
          }

          if (this.updateRecommendations.ipm || this.fixtureChange) {
            this.feedRate = this.setFeed(this.settingsHolder.tool.fluteCount, chipLoad, this.spindleSpeed);
          } else {
            this.feedRate = this.recommendation.ipm;
          }

          if (
            _.trim(this.settingsHolder.machine.maxIPM).length > 0 &&
            this.settingsHolder.machine.maxIPM < this.feedRate
          ) {
            this.feedRate = this.settingsHolder.machine.maxIPM;
          }

          let pathDiameter = this.settingsHolder.parameters.pathDiameter;

          let helixLength = math.sqrt(math.pow(math.PI * pathDiameter, 2) + math.pow(pitch, 2));
          let pathLength = (helixLength * holeDepth) / pitch + math.PI * pathDiameter;
          let cutTime = pathLength / this.feedRate;
          let cutMinutes = math.floor(cutTime);
          let cutSeconds = math.ceil(60 * (cutTime - cutMinutes));
          let cutVolume = math.PI * math.pow(holeDiameter / 2, 2) * holeDepth;
          let mrr = cutVolume / cutTime;

          let timeMinStr = cutMinutes;
          let timeSecStr = cutSeconds;

          if (cutSeconds < 10) {
            timeSecStr = '0' + timeSecStr;
          }

          this.recommendation = {
            rpm: parseInt(this.spindleSpeed.toFixed(0)),
            ipm: this.feedRate,
            sfm: parseInt(
              this.conversionsFormulas
                .convertRpmToSfm(this.spindleSpeed, this.getDiameterForSurfaceSpeed())
                .toString()
            ),
            ipt: this.conversionsFormulas.convertIpmToIpt(
              this.spindleSpeed,
              this.feedRate,
              this.settingsHolder.tool.fluteCount
            ),
            mrr: mrr,
            holeTime: timeMinStr + ':' + timeSecStr,
            chipThickness: chipLoad
          };
          this.renderRecommendations = true;
          if (this.isApp) {
            // emit to summary page
            this.commonService.notifyOther({
              option: 'summaryAlert',
              value: {
                settingsHolder: this.settingsHolder,
                recommendation: this.recommendation
              }
            });
          }
        }
      }
    }
  };

  private setupFactor = (millValue: string, workpiece: Material_, fixtureValue: number) => {
    let machineFactor = 1;
    let fixtureFactor = math.pow(fixtureValue / 100, math.pow(100 / (fixtureValue + (fixtureValue - 35) / 6), 2));

    if (millValue === 'Speed') {
      machineFactor = this.holderFactor * math.pow(1 / fixtureFactor, 1.0 / 8.0);
    } else {
      machineFactor = math.pow(
        this.holderFeedFactor * fixtureFactor * this.spindleFactor,
        1.0 / this.formulas.calculateMaterialStretch(workpiece)
      );
    }

    return machineFactor;
  };

  private setSpeed = (cutDia: number, surfaceSpeed: number, speedType: string = 'speed') => {
    let speed = this.speedAggression * this.conversionsFormulas.convertSfmToRpm(surfaceSpeed, cutDia);
    let maxRPM =
      this.settingsHolder.machine.maxRPM > 0 && !isNaN(this.settingsHolder.machine.maxRPM)
        ? this.settingsHolder.machine.maxRPM
        : 0;

    if (maxRPM > 0 && speed > maxRPM) {
      speed = maxRPM;
    } else if (speedType === 'speed') {
      let rpmPrecision = speed < 500 ? 1 : 5;
      speed = rpmPrecision * math.round(speed / rpmPrecision, 0);
    }

    return speed;
  };

  private setFeed = (fluteNo: number, chipLoad: number, speed: number, feedFactor: number = 1) => {
    let feed = this.feedAggression * this.conversionsFormulas.convertIptToIpm(speed, chipLoad * feedFactor, fluteNo);
    let ipmPrecision = math.pow(10, Math.trunc(math.log10(feed))) / 100;

    let maxIPM = this.settingsHolder.machine.maxIPM > 0 ? this.settingsHolder.machine.maxIPM : 0;

    if (maxIPM > 0 && feed > maxIPM) {
      feed = maxIPM;
    } else {
      feed = ipmPrecision * math.round(feed / ipmPrecision);
    }

    return feed;
  };

  private filletFeed = (feedRate: number) => {
    let partRad = 0; // this is always the wallrad, depending on what, it is divided by 2.

    if (this.recommendationAdjustment === 1) {
      const retParaRecValue = this.Recommendations.get('type1').get('insideCornerRadiusOfWall').value;
      if (_.trim(retParaRecValue) === '') {
        partRad = math.round(this.settingsHolder.tool.cutterDiameter * 1.5, 4);
      } else {
        partRad = parseFloat(retParaRecValue);
      }
    } else if (this.recommendationAdjustment === 2) {
      const retParaRecValue = this.Recommendations.get('type2').get('trochoidalSlotWidth').value;
      if (_.trim(retParaRecValue) === '') {
        partRad = math.round((this.settingsHolder.tool.cutterDiameter * 1.5) / 2, 4);
      } else {
        partRad = parseFloat(retParaRecValue) / 2;
      }
    }

    let radDepth = 0;

    if (this.insideCornerType === 'RDOC Inside Corner') {
      const roundFactor = this.formulas.emRoundFactor(this.settingsHolder.tool.cutterDiameter) / 5;
      const engageAng = this.settingsHolder.parameters.engagementAngle;
      radDepth =
        roundFactor *
        math.floor(
          this.conversionsFormulas.convertTeaToRdoc(engageAng, this.settingsHolder.tool.cutterDiameter, partRad) /
            roundFactor
        );
    } else {
      radDepth = this.settingsHolder.parameters.rdocdecimal;
    }

    const pathRad = partRad - this.settingsHolder.tool.cutterDiameter / 2;
    const linearFeed = feedRate ? feedRate : this.recommendation.ipm;
    let cornerFeed = 0;

    if (this.settingsHolder.currenttoolpath && this.settingsHolder.currenttoolpath.operationName === 'Finishing') {
      cornerFeed = (linearFeed * pathRad) / partRad;
    } else {
      cornerFeed =
        (2 * linearFeed * this.settingsHolder.parameters.rdocdecimal * pathRad) /
        (2 * partRad * radDepth - math.pow(radDepth, 2));
    }

    // cornerFeed < 10 => round to 2 decimals
    // 10 <= cornerFeed < 100 => round to 1 decimal
    // 100 <= cornerFeed => round to 0 decimals
    const ipmPrecision = cornerFeed < 10 ? 2 : cornerFeed < 100 ? 1 : 0;
    cornerFeed = math.floor(cornerFeed * math.pow(10, ipmPrecision)) / math.pow(10, ipmPrecision);

    // if its a wheel event, it causes the number to blink, not sure if there is a way to fix
    if (this.recommendationAdjustment === 1) {
      if (this.insideCornerType === 'RDOC Inside Corner') {
        this.recommendation.insideCornerValue = radDepth;
      } else if (this.insideCornerType === 'IPM Inside Corner') {
        this.recommendation.insideCornerValue = cornerFeed;
      }
    } else if (this.recommendationAdjustment === 2) {
      this.recommendation.ipmFeedInsideSlot = cornerFeed;
    }
  };

  maxCutDiaMessage = (mrr: number) => {
    let spindleTaper = this.settingsHolder.machine.spindle;

    spindleTaper = spindleTaper.replace('CV', '');
    spindleTaper = spindleTaper.replace('BT', '');
    spindleTaper = spindleTaper.replace('HSK', '');
    spindleTaper = spindleTaper.replace('NMTB', '');

    let spindleType = this.settingsHolder.machine.spindle.replace(spindleTaper, '');
    let spindleDia = 0;

    if (spindleType === 'HSK') {
      switch (spindleTaper) {
        case '25':
          spindleDia = 19.0;
          break;
        case '32':
          spindleDia = 24.0;
          break;
        case '40':
          spindleDia = 30.0;
          break;
        case '50':
          spindleDia = 38.0;
          break;
        case '63':
          spindleDia = 48.0;
          break;
        case '80':
          spindleDia = 60.0;
          break;
        case '100':
          spindleDia = 75.0;
          break;
        case '125':
          spindleDia = 95.0;
          break;
        case '160':
          spindleDia = 120.0;
          break;
      }
      spindleDia = spindleDia / 25.4;
    } else {
      switch (spindleTaper) {
        case '30':
          spindleDia = 1.25;
          break;
        case '35':
          spindleDia = 1.5;
          break;
        case '40':
          spindleDia = 1.75;
          break;
        case '45':
          spindleDia = 2.25;
          break;
        case '50':
          spindleDia = 2.75;
          break;
        case '60':
          spindleDia = 4.25;
          break;
      }
    }

    if (this.recommendation.mrr > 0) {
      if ((spindleDia * mrr) / (this.settingsHolder.tool.cutterDiameter * this.recommendation.mrr) < 2.5) {
        let messageStr = 'The selected tool and cutting parameters may';
        messageStr = messageStr + ' be too aggressive for the selected spindle.';
        messageStr = messageStr + ' Be mindful of excessive cutting forces when';
        messageStr =
          messageStr + ' using this tool with a ' + this.settingsHolder.machine.spindle + ' machining center.';
        messageStr = messageStr + ' Consider using a smaller tool, larger spindle,';
        messageStr = messageStr + ' or reduce the speed/feed.';

        let message: Message = {
          title: 'Too Aggressive!',
          message: messageStr
        };
        // let check if this material if in part of the tool first, cause a message saying the material and tool does not match might already exist
        let toolMaterials = this.settingsHolder.tool ? this.settingsHolder.tool.materialIds : undefined;
        let theMaterial = this.settingsHolder.material ? this.settingsHolder.material : undefined;

        if (toolMaterials && theMaterial) {
          if (!!toolMaterials.find(id => id === theMaterial.id)) {
            if (this.warningAlerts) {
              this.aggressiveWarning.emit();
              this.warningMessageOpen = true;
              this.utils.openDialog(
                message,
                'error',
                chose => {
                  this.warningMessageOpen = false;
                },
                chose => {
                  this.warningMessageOpen = false;
                }
              );
            }
          }
        }
      }
    }
  };

  filterRecommendations = (recs: RecommendationLabel[]): RecommendationLabel[] => {
    let filteredRecs: RecommendationLabel[] = [];
    filteredRecs = recs.filter(
      r => r.recommendationLabel !== 'Spindle Speed' && r.recommendationLabel !== 'Feed Per Minute'
    );
    return filteredRecs;
  };

  getMapModeTwoSliderLable = (recs: RecommendationLabel[], label: string): string => {
    let sliderValue: RecommendationLabel;
    if (label === 'speedSlider') {
      sliderValue = recs.find(r => r.recommendationLabel && r.recommendationLabel.toLowerCase() === 'spindle speed');
    } else {
      sliderValue = recs.find(r => r.recommendationLabel && r.recommendationLabel.toLowerCase() === 'feed per minute');
    }
    return sliderValue.recommendationLabel;
  };

  getMapModeTwoSliderValue = (recs: RecommendationLabel[], label: string): number => {
    let sliderValue: RecommendationLabel;
    if (label === 'speedSlider') {
      sliderValue = recs.find(r => r.recommendationLabel && r.recommendationLabel.toLowerCase() === 'spindle speed');
    } else {
      sliderValue = recs.find(r => r.recommendationLabel && r.recommendationLabel.toLowerCase() === 'feed per minute');
    }
    return this.calculateRecommendationValue(sliderValue);
  };

  private getDiameterForSurfaceSpeed = (): number => {
    return this.settingsHolder.currenttoolpath?.operationName?.toLowerCase() === 'chamfering' ? this.parameters.effectiveDiameter : this.settingsHolder.tool.cutterDiameter;
  }
}

class updateRecommendations {
  public rpm: boolean;
  public ipm: boolean;
  public sfm: boolean;
  public ipt: boolean;
  public mrr: boolean;
  public holeTime: boolean;
  public chipThickness: boolean;
  public rpmAdjusted: boolean;
  public ipmAdjusted: boolean;
  constructor(
    rpm: boolean = true,
    ipm: boolean = true,
    sfm: boolean = true,
    ipt: boolean = true,
    mrr: boolean = true,
    holeTime: boolean = true,
    chipThickness: boolean = true,
    rpmAdjusted: boolean = true,
    ipmAdjusted: boolean = true
  ) {
    this.rpm = rpm;
    this.ipm = ipm;
    this.sfm = sfm;
    this.ipt = ipt;
    this.mrr = mrr;
    this.holeTime = holeTime;
    this.chipThickness = chipThickness;
    this.rpmAdjusted = rpmAdjusted;
    this.ipmAdjusted = ipmAdjusted;
  }
}
